import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import styled from 'styled-components';

import amplitude from 'amplitude-js';
import { CountryItemPanel } from 'apps/auth/components/common/components/CountryItemPanel/CountryItemPanel';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { EmptyPage } from 'apps/auth/components/common/Layout/EmptyPage/EmptyPage';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { PanelContent, PanelFrame, PanelRow, PanelText } from 'apps/auth/components/common/Panel/styles';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import CountryOfResidencePopup from 'apps/auth/components/registration/CountryOfResidencePopup';
import SearchAddressNotFoundPopup from 'apps/auth/components/send/popups/SearchAddressNotFoundPopup';
import { getSofParamName } from 'apps/auth/components/utils/AmplitudeUtils';
import { DROPDOWN_LIST_ITEM_SELECTED, DROPDOWN_LIST_SHOWN, SOF_SCREEN_SHOWN, SOF_SELECTED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { FIELD_NAME, FIELD_VALUE, SOF } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { INCOME_TYPE, OCCUPATION, PURPOSE_OF_ACCOUNT, RESIDENT_COUNTRY, SOURCE_OF_INCOME } from 'apps/auth/constants/fieldsName';
import { ANTIFRAUD_HRJ, FINISH_REGISTRATION } from 'apps/auth/constants/formName';
import { toLowerCase } from 'apps/auth/helpers/utils';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { DropBoxService } from 'apps/auth/services/DropBoxService';

import { InputSearch } from './components/InputSearch';
import { SearchBar } from './components/SearchBar/SearchBar';
import media from '../../theme/media';
import ModalComponent from '../utils/ModalComponent';

const GroupTitle = styled.div`
    padding: 12px 24px;
    background: ${(props) => props.theme.grey_8_lc};
    color: ${(props) => props.theme.grey_72_lc};
    margin: 0 -24px;

    ${media.desktop1920} {
        padding: 16px 32px;
        margin: 0 -32px;
    }
`;

const InputDropBoxList = (props) => {
    const searchRef = useRef(null);
    const popupRef = useRef(null);
    const [itemsList, setItems] = useState(props.items);
    const [searchTerm, setSearchTerm] = useState('');
    const { bankNotInList } = usePageMessages();
    const [isFullViewSearch, toggleIsFullViewSearch] = useState(false);
    const { allCountries, phoneCountryId } = useExtra();
    const isSoF = (props.formName === ANTIFRAUD_HRJ && props.field?.name === SOURCE_OF_INCOME) || props.field?.name === INCOME_TYPE;
    const toggleSearch = (e, show) => {
        e.preventDefault();
        if (!show) {
            setItems(props.items);
            setSearchTerm('');
        }
        toggleIsFullViewSearch(show);
    };

    useEffect(() => {
        if (isShowSearch() && searchRef && searchRef.current) {
            setTimeout(() => {
                searchRef.current.focus();
            }, 100);
        }
        sendAdditionalFieldsEvent(DROPDOWN_LIST_SHOWN, { [FIELD_NAME]: props.field?.name });
        if (isSoF) {
            amplitude.getInstance().logEvent(SOF_SCREEN_SHOWN);
        }
    }, []);

    const sendAdditionalFieldsEvent = (eventName, params) => {
        if (props.formName === FINISH_REGISTRATION && (props.field?.name === OCCUPATION || props.field?.name === PURPOSE_OF_ACCOUNT)) {
            amplitude.getInstance().logEvent(eventName, params);
        }
    };

    const isShowSearch = () => {
        return Array.isArray(props.items) && props.items.length >= 10;
    };

    const search = (e) => {
        const term = e.target.value;
        const resultList = props.items.filter((item) => {
            return (
                term === undefined ||
                (item !== null && item.text !== null && toLowerCase(item.text).indexOf(toLowerCase(term)) >= 0) ||
                (item.alias !== null && item.alias.toLowerCase().indexOf(term.toLowerCase()) >= 0)
            );
        });
        setItems(resultList);
        setSearchTerm(term ?? '');
    };

    const getEmptyList = () => {
        const unlistedItem = DropBoxService.getUnlistedBankItem(props.items);

        const getEmptyPageButtonCaption = () => {
            if (unlistedItem) {
                return bankNotInList;
            } else if (props.showAddressNotFound) {
                return props.notFoundAddress;
            } else return null;
        };

        const getEmptyPageButtonClick = () => {
            if (unlistedItem) {
                return (e) => props.selectItem(e, unlistedItem);
            } else if (props.showAddressNotFound) {
                return (e) => {
                    e.preventDefault();
                    props.togglePopup(true, <SearchAddressNotFoundPopup iconName={props.addressNotFoundIcon} onClick={props.clickNotFoundBtn} />);
                };
            } else return null;
        };

        return (
            <>
                <EmptyPage
                    iconName="ghost"
                    emptyText={props.searchDropBoxEmpty}
                    emptyButtonCaption={getEmptyPageButtonCaption()}
                    emptyButtonClick={getEmptyPageButtonClick()}
                />
            </>
        );
    };

    const getGroupName = (groups, id) => {
        const group = groups.find((g) => g.id === id);
        return group ? group.value : '';
    };

    const getFlagIcon = (currentCountryCode, currentCountryId) => {
        if (currentCountryCode) {
            return currentCountryCode;
        } else {
            const currentCountry = allCountries.find(({ id }) => id === Number(currentCountryId));
            return currentCountry?.code;
        }
    };

    const getRegularList = (regularItems) => {
        let isAddGroupTitle = Array.isArray(props.groups);
        let prevGropId = null;

        const clearAddressFields = (fieldName, newCountrySelected) => {
            if (fieldName === RESIDENT_COUNTRY && newCountrySelected) {
                props.clearFieldsOnCountrySelect?.();
            }
        };

        return regularItems.map((item) => {
            const showGroupTitle = isAddGroupTitle && prevGropId !== item.group;
            const ulb = DropBoxService.isUnlistedBank(item);
            prevGropId = item.group;
            return (
                <React.Fragment key={`bank_name_from_list_${item.value}`}>
                    {showGroupTitle && <GroupTitle>{getGroupName(props.groups, item.group)}</GroupTitle>}
                    {props.hasCountryItemPanel ? (
                        <CountryItemPanel
                            flagIconName={getFlagIcon(item.countryCode, item.value)}
                            isSelectedCountry={props.selectedValue === item.value}
                            countryName={item.text}
                            query={searchTerm}
                            isDisabledPanel={Boolean(item.unavailable)}
                            onClick={(e) => {
                                if (item.unavailable) {
                                    popupRef.current?.open(
                                        <CountryOfResidencePopup
                                            popupRef={popupRef}
                                            text={<DangerouslyHtml dangerouslyHtml={item.unavailable} />}
                                            closeDropbox={props.closeDropbox}
                                        />,
                                    );
                                    return;
                                }
                                if (props.isCountrySelectionElementType) {
                                    popupRef.current?.open(
                                        <CountryOfResidencePopup
                                            popupRef={popupRef}
                                            selectedCountry={item.value}
                                            loginCountryId={phoneCountryId}
                                            residenceCountryId={item.value}
                                            closeDropbox={props.closeDropbox}
                                        />,
                                    );
                                } else {
                                    props.closeDropbox();
                                }
                                props.selectItem(e, item, true);
                                clearAddressFields(props.field?.name, props.selectedValue !== item.value);
                            }}
                        />
                    ) : (
                        <Panel
                            onClick={(e) => {
                                if (item.unavailable) {
                                    popupRef.current?.open(
                                        <CountryOfResidencePopup
                                            popupRef={popupRef}
                                            text={<DangerouslyHtml dangerouslyHtml={item.unavailable} />}
                                            closeDropbox={props.closeDropbox}
                                        />,
                                    );
                                    return;
                                }
                                props.selectItem(e, item);
                                sendAdditionalFieldsEvent(DROPDOWN_LIST_ITEM_SELECTED, { [FIELD_NAME]: props.field?.name, [FIELD_VALUE]: item.value });
                                isSoF ? amplitude.getInstance().logEvent(SOF_SELECTED, { [SOF]: getSofParamName(item.value) }) : null;
                            }}
                            isHoverable
                            negativeMargin
                        >
                            <PanelFrame>
                                <PanelContent>
                                    <PanelRow>
                                        <PanelText multiline>
                                            <Text color={ulb ? 'primary' : null}>{ulb ? bankNotInList : item.text}</Text>
                                        </PanelText>
                                    </PanelRow>
                                </PanelContent>
                                {props.selectedValue == item.value && <Icon name="check" color="primary" leftIndent="8px" />}
                            </PanelFrame>
                        </Panel>
                    )}
                </React.Fragment>
            );
        });
    };

    const getList = (items) => {
        if (!items || items.length === 0) {
            return getEmptyList();
        } else if (items && items.length > 0) {
            return getRegularList(items);
        }
    };

    return (
        <>
            {!isFullViewSearch ? (
                <ModalComponent isIconClose onCloseIconClick={props.onCloseIconClick} overflowHidden={props.dropdownModalOverflowHidden}>
                    <HeaderPanel title={props.selectTitle ? props.selectTitle : props.placeholder} subtitle={props.selectSubTitle} />
                    {isShowSearch() && <InputSearch placeholder={props.searchPlaceholder} toggle={toggleSearch} />}
                    {getList(itemsList)}
                </ModalComponent>
            ) : (
                <ModalComponent canCloseModal={false} overflowHidden={props.dropdownModalOverflowHidden}>
                    <SearchBar
                        placeholder={props.searchPlaceholder}
                        onChange={search}
                        id={`search_field_${props.name}_${props.id}`}
                        onCloseButtonClick={toggleSearch}
                        searchRef={searchRef}
                    />
                    {getList(itemsList)}
                </ModalComponent>
            )}
            <PopupNew ref={popupRef} overflowHidden={true} />
        </>
    );
};

InputDropBoxList.propTypes = {
    showAddressNotFound: PropTypes.bool,
    addressNotFoundIcon: PropTypes.string,
    clickNotFoundBtn: PropTypes.func,
    hasCountryItemPanel: PropTypes.bool,
    dropdownModalOverflowHidden: PropTypes.bool,
};

export default InputDropBoxList;
